import type { ScenarioId } from './scenarios'

export interface ScenarioMetrics {
  /** Additional nightly revenue unlocked (USD) */
  revenueImpact: number
  /** Aircraft moves required to execute this stacking plan */
  towMoves: number
  /** Minimum clearance between aircraft (feet) */
  safetyClearanceFt: number
  /** Staff headcount needed (driver + wing walkers) */
  crewRequired: number
  /** Time saved vs conventional manual planning (minutes) */
  timeSavingsMin: number
  /** Extra transient spots freed vs a simple pull-up approach */
  additionalCapacity: number
  /** Qualitative risk level */
  riskLevel: 'low' | 'medium' | 'high'
  /** Brief rationale shown under metrics */
  rationale: string
}

export const SCENARIO_METRICS: Record<ScenarioId, ScenarioMetrics> = {
  baseline: {
    revenueImpact: 0,
    towMoves: 0,
    safetyClearanceFt: 18,
    crewRequired: 0,
    timeSavingsMin: 0,
    additionalCapacity: 0,
    riskLevel: 'low',
    rationale: 'Current state. No optimization applied.',
  },

  max_yield_morning: {
    revenueImpact: 1450,
    towMoves: 8,
    safetyClearanceFt: 5,
    crewRequired: 6,
    timeSavingsMin: 45,
    additionalCapacity: 2,
    riskLevel: 'medium',
    rationale: 'Tight formation unlocks two Citation spots. Hero at the door — 3-min pull at 06:00. Requires full crew and wing walkers.',
  },

  max_yield_midday: {
    revenueImpact: 1650,
    towMoves: 9,
    safetyClearanceFt: 5,
    crewRequired: 6,
    timeSavingsMin: 50,
    additionalCapacity: 2,
    riskLevel: 'medium',
    rationale: 'Maximum revenue — door zone freed for two transient overnights. Clear corridor maintained for 13:00 pull. Best overall yield.',
  },

  max_yield_long_term: {
    revenueImpact: 4800,
    towMoves: 11,
    safetyClearanceFt: 4,
    crewRequired: 7,
    timeSavingsMin: 60,
    additionalCapacity: 3,
    riskLevel: 'high',
    rationale: 'Hero buried for 4 nights. Wings overlap turboprops at different elevations. Three extra transient spots × $600/night × 4 days.',
  },

  op_ease_morning: {
    revenueImpact: 400,
    towMoves: 2,
    safetyClearanceFt: 13,
    crewRequired: 2,
    timeSavingsMin: 10,
    additionalCapacity: 0,
    riskLevel: 'low',
    rationale: 'Minimal moves. Hero at the door with 4m clearance on each side. Night crew can handle it solo — no wing walkers needed.',
  },

  op_ease_midday: {
    revenueImpact: 500,
    towMoves: 3,
    safetyClearanceFt: 12,
    crewRequired: 3,
    timeSavingsMin: 15,
    additionalCapacity: 0,
    riskLevel: 'low',
    rationale: 'Comfortable spacing. Straight tow path — no complex maneuvers. Safe for an under-staffed mid-morning shift.',
  },
}
